/*
 * THE WORDS AND SHAPES THE ANALYTICS PAGE PRINTS (Inbound ▸ Analytics), from the
 * rows /api/analytics/* returns. Pure, so test/analytics-model.test.js can feed
 * it rows; the components only lay these out.
 *
 * Built against his "CRM Analytics" canvas (2026-10-18): monitor, laptop, phone,
 * the three live states and the side panel.
 */

export const ZONE = "Asia/Jerusalem"; // the page says "Israel time"

export const PROPS = {
  site: { label: "remotion.io", short: "site", phone: "site" },
  lp: { label: "Landing pages", short: "LP", phone: "LP" },
  book: { label: "Booking page", short: "booking", phone: "booking" },
};

/* A known person on one of these is the thing to act on. */
const SIGNAL_SITE_PATHS = ["/pricing"];

/* ------------------------------------------------------------ people -- */

// The CRM's own words for a stage (client/src/crm/ui.js BOARD_STAGE_LABELS),
// so a chip here reads the same as on the board.
const STAGE_WORDS = {
  new: "New",
  call_scheduled: "Call scheduled",
  in_progress: "In progress",
  evaluating: "Deciding internally",
  proposal_sent: "Pricing sent",
  agreement: "Agreement + legal",
  negotiation: "Negotiation",
};
const STATUS_WORDS = {
  client: "Client",
  churned: "Churned",
  lost: "Lost",
  not_a_fit: "Not a fit",
  recycled: "Recycled",
  advisory: "Advisory",
  vendor: "Vendor",
};

/* The chip beside a name: the company's status when it is not a live prospect, else its stage. */
export function stageChip(person) {
  if (!person) return null;
  const status = person.company_status;
  if (status && status !== "prospect" && STATUS_WORDS[status]) return { key: status, label: STATUS_WORDS[status] };
  const stage = person.pipeline_stage;
  if (stage && STAGE_WORDS[stage]) return { key: stage, label: STAGE_WORDS[stage] };
  return null;
}

/* "Ciridae", or "VP Marketing · Token" for the side panel. */
export function personLine(person, { withTitle = false } = {}) {
  if (!person) return "";
  return [withTitle ? person.title : null, person.company].filter(Boolean).join(" · ");
}

/* ----------------------------------------------------------- country -- */

/*
 * A visit's country (src/analytics/geo.js): the flag is the two regional
 * indicator letters, drawn by the Twemoji flag font (analytics.css) because
 * Windows has no flag emoji; the name is the browser's own, in English.
 */
export function flagOf(code) {
  if (!code || !/^[A-Z]{2}$/.test(code)) return "";
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

let regionNames = null;
export function countryName(code) {
  if (!code) return "";
  try {
    if (!regionNames) regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
}

/* The first day visits carry a country: src/analytics/geo.js went live 20 Oct 2026, 08:12 UTC. */
export const COUNTRY_FROM = "20 Oct";

/* ----------------------------------------------------------- sources -- */

/*
 * A source as the rows print it: a pill for the kind that needs one, and the
 * words beside it. Ads lead in red, a tagged link gets an outline pill, a
 * referrer is just its host, Direct is greyed.
 */
export function sourceView(source) {
  const s = source || { kind: "direct", label: "Direct", name: "Direct" };
  const name = s.name || s.label || "Direct";
  if (s.kind === "ads") return { kind: "ad", pill: "LinkedIn ad", text: name };
  if (s.kind === "campaign") return { kind: "tagged", pill: "Tagged link", text: name };
  if (s.kind === "referral") return { kind: "referral", pill: null, text: name };
  return { kind: "direct", pill: null, text: "Direct" };
}

export const SOURCE_KIND_WORDS = { ad: "ad", tagged: "tagged link", referral: "referral", direct: "direct" };

/* ------------------------------------------------------------- where -- */

function variantWord(page) {
  return page && page.variant ? page.variant : null;
}

/*
 * Where a row is, in the two lengths the page uses: the live list's full one
 * ("LinkedIn Ads landing page · variant d") and the log's short one
 * ("LinkedIn Ads LP · d").
 */
export function whereLabel(row, { short = false } = {}) {
  if (!row) return "";
  if (row.property === "site") {
    const path = row.path || (row.pages && row.pages[row.pages.length - 1]) || "/";
    return `remotion.io ${path}`;
  }
  if (row.property === "lp") {
    const title = (row.page && row.page.title) || "Landing page";
    const v = variantWord(row.page);
    if (short) return `${title} LP${v ? ` · ${v}` : ""}`;
    return `${title} landing page${v ? ` · variant ${v}` : ""}`;
  }
  // A meeting type's own link (/book/chat) says which; the intro call is just the page.
  const slug = row.page && row.page.slug;
  return `Booking page${slug && slug !== "intro" ? ` · ${slug}` : ""}`;
}

/* The pages a live row has seen, the current one last. */
export function trail(entry) {
  const t = (entry && entry.trail) || [];
  const paths = t.map((x) => x.path);
  const out = [];
  for (const p of paths) if (out[out.length - 1] !== p) out.push(p);
  return out.length ? out.slice(-4) : [entry && entry.path ? entry.path : "/"];
}

/* ------------------------------------------------------------ live -- */

export function isSignal(entry) {
  if (!entry || !entry.person) return null;
  if (entry.property === "book") return "on the booking page";
  if (entry.property === "site" && SIGNAL_SITE_PATHS.includes(entry.path)) return `on the ${entry.path.slice(1)} page`;
  return null;
}

export function isSignalPage(path) {
  return SIGNAL_SITE_PATHS.includes(path);
}

const JUST_ARRIVED_S = 45;

/*
 * How a live row looks and what its flag says:
 *   knownNew  someone we know, just arrived: the strongest row on the page
 *   known     someone we know
 *   anonNew   a stranger, just arrived: a soft highlight that fades
 *   anon      everyone else
 */
export function liveLook(entry, now = Date.now()) {
  const fresh = now - new Date(entry.since).getTime() < JUST_ARRIVED_S * 1000;
  const signal = isSignal(entry);
  if (entry.person) {
    if (fresh) return { look: "knownNew", flag: "Just arrived", flagText: signal || `on ${whereLabel(entry)}` };
    return { look: "known", flag: signal ? "Signal" : null, flagText: signal || "" };
  }
  return fresh ? { look: "anonNew", flag: "New", flagText: "" } : { look: "anon", flag: null, flagText: "" };
}

/* Known people first, then whoever has been on longest. */
export function sortLive(list) {
  return [...list].sort((a, b) => !!b.person - !!a.person || new Date(a.landed_at || a.since) - new Date(b.landed_at || b.since));
}

/* Apply one SSE "presence" event to the list. */
export function applyPresence(list, { type, entry }) {
  if (!entry || !entry.id) return list;
  const rest = list.filter((e) => e.id !== entry.id);
  if (type === "leave") return rest;
  return sortLive([...rest, entry]);
}

export function liveCounts(list) {
  const n = { site: 0, lp: 0, book: 0 };
  for (const e of list) if (n[e.property] !== undefined) n[e.property] += 1;
  return n;
}

export function liveHeadline(n) {
  if (!n) return "Listening · nobody on the sites";
  return `${n} ${n === 1 ? "person" : "people"} on the sites`;
}

/* "12 min ago · LinkedIn Ads landing page" */
export function lastVisitLine(recent, now = Date.now()) {
  if (!recent || !recent.last_visit) return null;
  const v = recent.last_visit;
  const where = v.property === "site" ? `remotion.io ${v.label || "/"}` : v.property === "lp" ? `${v.label || "Landing page"} landing page` : "Booking page";
  return `Last visit ${agoWords(v.at, now)} · ${where}`;
}

export function todayLine(recent) {
  if (!recent) return null;
  const n = recent.today_visits || 0;
  const usual = recent.usual_per_day;
  return `Today so far: ${n} ${n === 1 ? "visit" : "visits"}${usual != null ? ` · usually about ${usual} a day` : ""}`;
}

function agoWords(at, now) {
  const s = Math.max(0, Math.round((now - new Date(at).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ${h === 1 ? "hour" : "hours"} ago`;
  const d = Math.round(h / 24);
  return `${d} ${d === 1 ? "day" : "days"} ago`;
}

/* ------------------------------------------------------------- time -- */

/* 1:52, 12:05, 1:02:09 */
export function clock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const two = (x) => (x < 10 ? "0" : "") + x;
  return h ? `${h}:${two(m)}:${two(r)}` : `${m}:${two(r)}`;
}

export function secondsSince(at, now = Date.now()) {
  return Math.max(0, Math.floor((now - new Date(at).getTime()) / 1000));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Month names from our own list: en-GB writes "Sept", which the canvas does not.
const parts = (d) => {
  const f = new Intl.DateTimeFormat("en-GB", { timeZone: ZONE, year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const o = {};
  for (const p of f.formatToParts(new Date(d))) o[p.type] = p.value;
  o.month = MONTHS[Number(o.month) - 1];
  o.day = String(Number(o.day));
  return o;
};

export function dayKey(d) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(d));
}

/* "Today 08:49", "Yesterday 22:54", "15 Oct 18:13", "15 Oct 2025 18:13" */
export function whenLabel(at, now = Date.now()) {
  const p = parts(at);
  const time = `${p.hour}:${p.minute}`;
  const k = dayKey(at);
  if (k === dayKey(now)) return `Today ${time}`;
  if (k === dayKey(now - 86400000)) return `Yesterday ${time}`;
  const year = parts(now).year === p.year ? "" : ` ${p.year}`;
  return `${p.day} ${p.month}${year} ${time}`;
}

/* "18 Sep" */
export function dayMonth(d) {
  const p = parts(d);
  return `${p.day} ${p.month}`;
}

export function rangeLabel(range) {
  if (!range) return "";
  // `to` is an exclusive end; a range ending at midnight shows the day before.
  const to = new Date(new Date(range.to).getTime() - 1);
  return `${dayMonth(range.from)} – ${dayMonth(to)}`;
}

/* The preset buttons. Each is a [from, to) in the reader's days. */
export const PRESETS = [
  { key: "today", label: "Today", days: 0 },
  { key: "7", label: "7 days", days: 7 },
  { key: "30", label: "30 days", days: 30 },
  { key: "90", label: "90 days", days: 90 },
];

export function presetRange(key, now = new Date()) {
  const p = PRESETS.find((x) => x.key === key) || PRESETS[2];
  if (p.days === 0) return { from: startOfDay(now).toISOString(), to: now.toISOString() };
  return { from: new Date(now.getTime() - p.days * 86400000).toISOString(), to: now.toISOString() };
}

export function presetTitle(key, range) {
  if (key === "today") return "Today";
  const p = PRESETS.find((x) => x.key === key);
  return p ? `Last ${p.label}` : rangeLabel(range);
}

/* Midnight in Israel, as an instant. */
export function startOfDay(now = new Date()) {
  const k = dayKey(now);
  for (const off of [2, 3]) {
    const guess = new Date(`${k}T00:00:00+0${off}:00`);
    if (dayKey(guess) === k && dayKey(new Date(guess.getTime() - 1)) !== k) return guess;
  }
  return new Date(`${k}T00:00:00+03:00`);
}

/* A custom range from two date inputs (YYYY-MM-DD), both days whole. */
export function customRange(fromDay, toDay) {
  if (!fromDay || !toDay) return null;
  const a = startOfDay(new Date(`${fromDay}T12:00:00Z`));
  const b = startOfDay(new Date(`${toDay}T12:00:00Z`));
  const end = new Date(b.getTime() + 86400000 + 3600000);
  const to = startOfDay(end);
  return a < to ? { from: a.toISOString(), to: to.toISOString() } : null;
}

/* ------------------------------------------------------------ chart -- */

const TRACK_NAMES = { book: "Booking page", lp: "Landing pages", site: "remotion.io" };

/* The top of the axis: four equal steps of a round size (25 → 100, 50 → 200). */
function niceMax(v) {
  for (const step of [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000]) if (v <= step * 4) return step * 4;
  return Math.ceil(v / 4000) * 4000;
}

/*
 * The visits-per-day chart: one column per day, stacked by property, the ad
 * visits beside it, the forms and calls in two lanes under it. Days before any
 * property was counted are "not tracked", never zero, and each property's first
 * day carries a marker.
 */
export function chartModel(days, trackedFrom, now = Date.now()) {
  const list = days || [];
  const firstTracked = Object.values(trackedFrom || {})
    .filter(Boolean)
    .map((d) => dayKey(d))
    .sort()[0];
  const max = niceMax(Math.max(0, ...list.map((d) => d.site + d.lp + d.book)));
  const today = dayKey(now);
  // One marker per day: two properties first counted the same day share it.
  const byDay = new Map();
  for (const [k, at] of Object.entries(trackedFrom || {})) {
    if (!at) continue;
    const day = dayKey(at);
    byDay.set(day, [...(byDay.get(day) || []), k]);
  }
  const markers = [...byDay.entries()].map(([day, keys]) => ({ keys, day })).sort((a, b) => a.day.localeCompare(b.day));
  const cols = list.map((d, i) => {
    const tracked = !!firstTracked && d.day >= firstTracked;
    const m = markers.findIndex((x) => x.day === d.day);
    const total = d.site + d.lp + d.book;
    return {
      ...d,
      tracked,
      total,
      pct: { site: (d.site / max) * 100, lp: (d.lp / max) * 100, book: (d.book / max) * 100, ads: (d.from_ads / max) * 100 },
      marker: m >= 0 ? { text: `${markerNames(markers[m].keys)}${m === 0 ? " tracked" : ""} from ${shortDay(d.day)}`, row: markers.slice(0, m).filter((x) => list.some((y) => y.day === x.day)).length } : null,
      tip: tracked
        ? `${shortDay(d.day)}${d.day === today ? " (today, so far)" : ""}: ${total} visits · remotion.io ${d.site} · landing pages ${d.lp} · booking page ${d.book} · from ads ${d.from_ads}`
        : `${shortDay(d.day)}: not tracked yet`,
      tick: null,
      isToday: d.day === today,
      index: i,
    };
  });
  // Ticks: the first day, every seventh after it, and today, with no two crowding.
  const tickAt = new Set([0]);
  for (let i = 7; i < cols.length; i += 7) tickAt.add(i);
  if (cols.length) {
    const last = cols.length - 1;
    for (const t of [...tickAt]) if (last - t < 3 && t !== 0) tickAt.delete(t);
    tickAt.add(last);
  }
  for (const i of tickAt) if (cols[i]) cols[i].tick = cols[i].isToday ? "Today" : shortDay(cols[i].day);
  const untracked = cols.filter((c) => !c.tracked).length;
  return {
    cols,
    max,
    yTicks: [max, (max * 3) / 4, max / 2, max / 4, 0],
    untrackedPct: cols.length ? (untracked / cols.length) * 100 : 0,
    forms: cols.reduce((n, c) => n + c.forms, 0),
    calls: cols.reduce((n, c) => n + c.calls, 0),
  };
}

function markerNames(keys) {
  const names = ["book", "lp", "site"].filter((k) => keys.includes(k)).map((k) => TRACK_NAMES[k]);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
}

function shortDay(key) {
  const [, m, d] = key.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}

/* "Booking page from 1 Oct · landing pages from 7 Oct · remotion.io from 8 Oct" */
export function trackedLine(trackedFrom) {
  const order = Object.entries(trackedFrom || {})
    .filter(([, at]) => at)
    .sort((a, b) => new Date(a[1]) - new Date(b[1]));
  if (!order.length) return null;
  const words = { book: "booking page", lp: "landing pages", site: "remotion.io" };
  return order.map(([k, at]) => `${words[k]} from ${dayMonth(at)}`).join(" · ");
}

/* ----------------------------------------------------------- tables -- */

export function kpis(o) {
  const t = o.totals;
  const site = o.properties.find((p) => p.kind === "site") || { forms: 0 };
  const lpForms = o.properties.filter((p) => p.kind === "lp").reduce((n, p) => n + p.forms, 0);
  const pct = t.visits ? Math.round((t.from_ads / t.visits) * 100) : 0;
  return [
    { key: "visits", label: "Visits", value: t.visits, sub: `remotion.io ${t.site} · Landing pages ${t.lp} · Booking page ${t.book}`, phoneSub: `remotion.io ${t.site} · LP ${t.lp} · Booking ${t.book}` },
    { key: "ads", label: "From LinkedIn ads", value: t.from_ads, sub: `${pct}% of visits`, phoneSub: `${pct}% of visits` },
    { key: "forms", label: "Forms sent", value: t.forms, sub: `${site.forms} on remotion.io · ${lpForms} on the landing ${lpForms === 1 ? "page" : "pages"}`, phoneSub: `${site.forms} site · ${lpForms} LP` },
    { key: "calls", label: "Calls booked", value: t.calls, sub: "on the booking page", phoneSub: "booking page" },
    { key: "known", label: "Named visitors", value: t.known_sessions, sub: "sessions by people the CRM knows", phoneSub: "sessions we know" },
  ];
}

/* "0:37", or the not-yet line when nothing in range was timed. */
export function avgTime(p) {
  return p && p.avg_seconds != null ? clock(p.avg_seconds) : null;
}

/*
 * Conversion rate: people who converted over people who came. A form is the
 * conversion on remotion.io and the landing pages (a landing-page call is a
 * form that went on to book); a booked call is the one on the booking page.
 * Null when nobody came, so the cell reads "–", never "0%".
 */
export function convRate(p, kind = p && p.kind) {
  if (!p || !p.people) return null;
  const done = kind === "book" ? p.calls || 0 : p.forms || 0;
  const r = (done / p.people) * 100;
  const pct = r === 0 ? "0%" : r < 10 ? `${r.toFixed(1).replace(/\.0$/, "")}%` : `${Math.round(r)}%`;
  const what = kind === "book" ? (done === 1 ? "call booked" : "calls booked") : done === 1 ? "form sent" : "forms sent";
  return { pct, title: `${done} ${what} ÷ ${p.people} ${p.people === 1 ? "person" : "people"}` };
}

/* "· in the 50/50 split", "· in the split since 15 Oct", "· out of the split" */
export function variantNote(v, variants, range) {
  if (!v.in_split) return "out of the split";
  const live = variants.filter((x) => x.in_split);
  if (live.length === 1) return "the only live version";
  const newest = live.reduce((a, b) => (new Date(a.live_since || 0) > new Date(b.live_since || 0) ? a : b));
  if (v === newest && v.live_since && range && new Date(v.live_since) > new Date(range.from)) return `in the split since ${dayMonth(v.live_since)}`;
  return `in the ${live.map((x) => x.share).join("/")} split`;
}

/*
 * The sources table's bars. When one source dwarfs the rest (Direct does), the
 * scale is set by the second and the first bar is drawn broken, so the ads are
 * not squeezed to slivers beside it.
 */
export function sourceRows(sources) {
  const list = sources || [];
  const [a, b] = [list[0] ? list[0].visits : 0, list[1] ? list[1].visits : 0];
  const scale = a > 2 * b && b > 0 ? Math.ceil(b * 1.1) : Math.max(a, 1);
  return list.map((s) => {
    const v = sourceView(s);
    const to = [
      ["site", s.site],
      ["lp", s.lp],
      ["book", s.book],
    ]
      .filter(([, n]) => n)
      .map(([k, n]) => ({ key: k, label: PROPS[k].short, n }));
    return {
      ...s,
      view: v,
      kindWord: SOURCE_KIND_WORDS[v.kind],
      title: v.kind === "ad" ? `LinkedIn ad: ${v.text}` : v.text,
      note: v.kind === "direct" ? "includes the team and link checkers" : null,
      barPct: Math.min(100, (s.visits / scale) * 100),
      broken: s.visits > scale,
      to,
    };
  });
}

/*
 * BREAKDOWNS ▸ COUNTRY. One row per country, the most visits first, and the
 * visits with no country ("not recorded": before COUNTRY_FROM, or an address
 * the table has no country for) last and kept out of the shares, so a range
 * that starts before 20 Oct does not read as "60% nowhere".
 */
export function countryRows(countries) {
  const list = countries || [];
  const known = list.filter((c) => c.code);
  const recorded = known.reduce((n, c) => n + c.visits, 0);
  const max = Math.max(1, ...known.map((c) => c.visits));
  return list
    .map((c) => ({
      ...c,
      name: c.code ? countryName(c.code) : "Not recorded",
      share: c.code && recorded ? Math.round((c.visits / recorded) * 100) : null,
      barPct: c.code ? Math.max(2, (c.visits / max) * 100) : 0,
      adPct: c.code && c.visits ? (c.from_ads / c.visits) * 100 : 0,
    }))
    .sort((a, b) => !a.code - !b.code || b.visits - a.visits || a.name.localeCompare(b.name));
}

/* "21 countries · 95 visits with a country · 6 without" */
export function countrySummary(countries) {
  const list = countries || [];
  const known = list.filter((c) => c.code);
  const none = list.filter((c) => !c.code).reduce((n, c) => n + c.visits, 0);
  const recorded = known.reduce((n, c) => n + c.visits, 0);
  const parts = [`${known.length} ${known.length === 1 ? "country" : "countries"}`, `${recorded} ${recorded === 1 ? "visit" : "visits"} with a country`];
  if (none) parts.push(`${none} without`);
  return parts.join(" · ");
}

/*
 * BREAKDOWNS ▸ AD CAMPAIGN. The ad sets (utm_campaign) under their LinkedIn
 * campaign group, the busiest group first; an ad set no visit tagged with a
 * group sits under "No group tag" at the end. Bars are against the busiest ad
 * set across all groups, so two groups read on one scale.
 *
 * SPEND comes from the auditor per ad set (src/analytics/adSpend.js). `spend`
 * on an ad set is null where LinkedIn has no figure for it; a group adds up
 * only the sets that have one, and its costs divide that by the visits, forms
 * and calls of those same sets, so an unpriced test link never makes a group
 * look cheaper.
 */
export const NO_GROUP = "No group tag";

const perOne = (spend, n) => (n ? spend / n : null);

export function campaignGroups(campaigns) {
  const list = campaigns || [];
  const max = Math.max(1, ...list.map((c) => c.visits));
  const groups = new Map();
  for (const c of list) {
    const key = c.group || "";
    if (!groups.has(key)) {
      groups.set(key, { key, name: c.group || NO_GROUP, visits: 0, forms: 0, calls: 0, spend: null, priced: { visits: 0, forms: 0, calls: 0 }, adSets: [] });
    }
    const g = groups.get(key);
    g.visits += c.visits;
    g.forms += c.forms;
    g.calls += c.calls;
    if (c.spend != null) {
      g.spend = (g.spend || 0) + c.spend;
      g.priced.visits += c.visits;
      g.priced.forms += c.forms;
      g.priced.calls += c.calls;
    }
    g.adSets.push({
      ...c,
      name: c.ad_set || "Untagged ad",
      barPct: c.visits ? Math.max(2, (c.visits / max) * 100) : 0,
      topCountries: (c.countries || []).slice(0, 3),
      moreCountries: Math.max(0, (c.countries || []).length - 3),
    });
  }
  return [...groups.values()]
    .map(({ priced, ...g }) => ({
      ...g,
      adSets: g.adSets.sort((a, b) => b.visits - a.visits || (b.spend || 0) - (a.spend || 0) || a.name.localeCompare(b.name)),
      cost_per_visit: g.spend == null ? null : perOne(g.spend, priced.visits),
      cost_per_form: g.spend == null ? null : perOne(g.spend, priced.forms),
      cost_per_call: g.spend == null ? null : perOne(g.spend, priced.calls),
    }))
    .sort((a, b) => !a.key - !b.key || b.visits - a.visits || (b.spend || 0) - (a.spend || 0) || a.name.localeCompare(b.name));
}

/* "$1.2K" past a thousand, "$12" under, "$4.50" under ten (a cost per visit of $3 and of $3.49 are different bids), "$0" for nothing. */
export function usd(n) {
  if (n == null || !Number.isFinite(Number(n))) return null;
  const v = Number(n);
  if (v === 0) return "$0";
  if (Math.abs(v) >= 1000) return `$${(Math.round(v / 100) / 10).toLocaleString("en-US")}K`;
  if (Math.abs(v) >= 10) return `$${Math.round(v)}`;
  return `$${v.toFixed(2)}`;
}

/* "115 visits from ads · 9 ad sets · 3 of 6 forms · 1 of 10 calls · $4.1K spent" */
export function campaignSummary(campaigns, totals, adSpend = null) {
  const list = campaigns || [];
  const n = (k) => list.reduce((x, c) => x + (c[k] || 0), 0);
  const visits = n("visits");
  const sets = list.filter((c) => c.ad_set && c.visits).length;
  const parts = [`${visits} ${visits === 1 ? "visit" : "visits"} from ads`, `${sets} ad ${sets === 1 ? "set" : "sets"}`];
  if (totals) {
    parts.push(`${n("forms")} of ${totals.forms} ${totals.forms === 1 ? "form" : "forms"}`);
    parts.push(`${n("calls")} of ${totals.calls} ${totals.calls === 1 ? "call" : "calls"}`);
  }
  if (adSpend) parts.push(`${usd(adSpend.total)} spent`);
  return parts.join(" · ");
}

/* "Spend from LinkedIn, stored nightly, through 20 Oct." */
export function spendNote(adSpend) {
  if (!adSpend) return "Spend per ad set is not available right now, so no cost is shown.";
  const through = adSpend.through ? `, through ${dayMonth(adSpend.through)}` : "";
  return `Spend is LinkedIn's, per ad set by its name, stored nightly${through}. A group's costs count only the ad sets LinkedIn has a figure for.`;
}

export function pageRows(pages) {
  const list = pages || [];
  const max = Math.max(1, ...list.map((p) => p.visits));
  return list.map((p) => ({
    ...p,
    signal: isSignalPage(p.path),
    visitPct: Math.max(3, (p.visits / max) * 100),
    landPct: p.visits ? (p.entrances / p.visits) * 100 : 0,
  }));
}

/*
 * The log's rows. A known person's visits to one property on one day fold
 * into one row ("5 visits that day"): Morgane opening her booking link five
 * times reads as one afternoon, not five strangers.
 */
export function logRows(sessions, now = Date.now()) {
  const out = [];
  for (const s of sessions || []) {
    const prev = out[out.length - 1];
    if (prev && s.person && prev.person && prev.person.contact_id === s.person.contact_id && prev.property === s.property && dayKey(prev.started_at) === dayKey(s.started_at)) {
      prev.folded += 1;
      continue;
    }
    out.push({ ...s, folded: 1 });
  }
  return out.map((s) => ({
    ...s,
    when: whenLabel(s.started_at, now),
    where: whereLabel(s, { short: true }),
    extraWhere: s.property === "site" && s.pages.length > 1 ? `${s.pages.length} pages` : s.views > 1 ? `${s.views} views` : null,
    extraWho: s.folded > 1 ? `${s.folded} visits that day` : null,
    source: sourceView(s.source),
    stayed: s.on_now
      ? { text: "on now", kind: "live" }
      : s.seconds != null
        ? // Gone before the first beat after the page view: shorter than a beat, not zero.
          { text: s.seconds < 15 ? "under 15s" : clock(s.seconds), kind: "timed" }
        : { text: "not measured", kind: "none" },
  }));
}

/*
 * WHAT A LANDING PAGE VISITOR DID (migration 085), in words: how far they
 * scrolled and what they clicked, for the log's side panel and the Landing
 * pages tab. A field is "typed in" because the page notes the first focus on
 * it; what they typed is never recorded.
 */
export function clickWords(c) {
  if (!c) return "";
  if (c.k === "field") return `${c.l} field`;
  if (c.k === "other") return `“${c.l}”`;
  return c.l;
}

export function scrollWords(did) {
  if (!did || did.scroll_pct == null) return null;
  return did.section ? `Scrolled ${did.scroll_pct}%, to “${did.section}”` : `Scrolled ${did.scroll_pct}%`;
}

export function didWords(did) {
  if (!did) return null;
  const clicks = Array.isArray(did.clicks) ? did.clicks : [];
  return {
    scroll: scrollWords(did),
    clicks: clicks.length ? clicks.map(clickWords) : [],
    none: !clicks.length,
  };
}
